import React, { useState } from "react";
import { ArrowRight } from "lucide-react";
import { HC_SERVICES } from "../data/hcServicesData";
import FullScreenDetailModal from "./FullScreenDetailModal";
import ServiceDetailContent from "./ServiceDetailContent";

function buildModalSections(service) {
  if (!service?.detail) return [];
  const prefix = "modal-section";
  const out = [];
  service.detail.sections?.forEach((s, i) => {
    out.push({
      id: `${prefix}-${i}`,
      label: s.shortLabel || (s.heading && s.heading.split(/\s+/).slice(0, 2).join(" ")) || `Abschnitt ${i + 1}`,
    });
  });
  if (service.detail.cta) {
    out.push({ id: `${prefix}-cta`, label: service.detail.cta.shortLabel || "Jetzt anfragen" });
  }
  if (service.detail.bottomImage) {
    out.push({
      id: `${prefix}-bottom-image`,
      label: service.detail.bottomImage.shortLabel || "Bild",
    });
  }
  if (service.detail.bottomImages?.images?.length) {
    out.push({
      id: `${prefix}-bottom-images`,
      label: service.detail.bottomImages.shortLabel || "Bilder",
    });
  }
  return out;
}

export default function HCServices({
  services = HC_SERVICES,
  title = "Unsere Dienstleistungen für die Gesundheitsbranche",
  gridColsClass = "sm:grid-cols-2 lg:grid-cols-4",
  pinnedId = "garantien",
}) {
  const [activeService, setActiveService] = useState(null);

  return (
    <section className="w-full">
      <div className="container-app py-10">
        <div className="mb-6">
          <h2 className="text-xl sm:text-3xl md:text-4xl font-semibold md:font-medium text-gray-900">
            {title}
          </h2>
        </div>

        <div
          className={`
            flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory gap-4 pb-2
            -mx-4 px-4
            md:mx-0 md:px-0 md:grid md:overflow-visible md:snap-none md:gap-6 ${gridColsClass}
          `}
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {services.map((s, idx) => (
            <ServiceCard
              key={s.id || s.title}
              item={s}
              index={idx}
              onOpen={() => setActiveService(s)}
              pinnedId={pinnedId}
            />
          ))}
        </div>
      </div>

      <FullScreenDetailModal
        open={!!activeService}
        onClose={() => setActiveService(null)}
        title={activeService?.title}
        image={activeService?.image}
        sections={buildModalSections(activeService)}
      >
        {activeService && (
          <ServiceDetailContent
            service={activeService}
            onClose={() => setActiveService(null)}
          />
        )}
      </FullScreenDetailModal>
    </section>
  );
}

function ServiceCard({ item, index, onOpen, pinnedId }) {
  // Optionally keep one card visually \"pinned\" open (medical: Garantien)
  const isPinnedOpen = pinnedId && item.id === pinnedId;

  return (
    <article
      className="
        group relative overflow-hidden rounded-3xl btn-bg
        shadow-[0_10px_25px_rgba(0,0,0,0.06)]
        h-[320px]
        flex-none w-[85%] min-w-[85%] snap-center
        md:flex-initial md:w-auto md:min-w-0
      "
    >
      <div className="h-full">
        <div className="relative h-[190px] w-full overflow-hidden">
          <img
            src={item.image}
            alt={item.title}
            className={[
              "h-full w-full object-cover transition-transform duration-700 ease-out",
              isPinnedOpen ? "scale-[1.03]" : "group-hover:scale-[1.03]",
            ].join(" ")}
            loading="lazy"
          />
          {/* Image overlay: full on mobile so image hidden; same on desktop hover/pinned */}
          <div
            className={[
              "absolute inset-0 btn-bg transition-opacity duration-300",
              isPinnedOpen
                ? "opacity-100"
                : "opacity-100 md:opacity-0 md:group-hover:opacity-100",
            ].join(" ")}
          />
        </div>

        <div className="px-6 py-5">
          <h3 className="text-2xl font-[500] text-gray-900 leading-snug line-clamp-2">
            {item.title}
          </h3>
        </div>
      </div>

      {/* Slide-up panel: always open on mobile (no hover); desktop hover or pinned */}
      <div
        className={[
          "absolute inset-0 transition-transform duration-[800ms]",
          "ease-[cubic-bezier(0.22,1,0.36,1)] transform-gpu will-change-transform",
          isPinnedOpen
            ? "translate-y-0"
            : "translate-y-0 md:translate-y-full md:group-hover:translate-y-0",
        ].join(" ")}
      >
        <div className="h-full btn-bg px-6 pt-6 pb-6 flex flex-col">
          <h4 className="text-xl font-medium text-gray-900 leading-snug">
            {item.title}
          </h4>

          <p className="mt-3 text-sm leading-relaxed text-gray-700">
            {item.desc}
          </p>

          <button
            type="button"
            onClick={onOpen}
            className="group btn mt-auto text-gray-900 bg-[#f1eeea] hover:bg-[#e7e2dc]"
          >
            Erfahren Sie mehr
            <ArrowRight className="h-5 w-6 shrink-0 transition-transform duration-200 ease-out group-hover:translate-x-0.5" aria-hidden />
          </button>
        </div>
      </div>
    </article>
  );
}
